/**
 * MODAL: TOKENS INSUFFISANTS
 * Redirige vers la page /pricing pour voir tous les plans
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Zap, FileText, ArrowRight } from 'lucide-react';

interface InsufficientTokensDeepReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  requiredTokens: number;
  selectedInsightsCount: number;
  subscriptionTier?: 'free' | 'pro' | 'business' | 'enterprise';
}

export default function InsufficientTokensDeepReportModal({
  isOpen,
  onClose,
  currentBalance,
  requiredTokens,
  selectedInsightsCount,
  subscriptionTier = 'free',
}: InsufficientTokensDeepReportModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleNavigateToPricing = () => {
    navigate('/billing');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-theme-base/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative max-w-lg w-full mx-4 bg-gradient-to-br bg-theme-surface via-neutral-950 to-black border border-orange-600/30 rounded-2xl shadow-2xl shadow-blue-500/20 overflow-hidden">
        {/* Header avec gradient */}
        <div className="relative h-32 bg-gradient-to-r from-orange-600/20 to-purple-500/20 border-b border-orange-600/20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDIwIDAgTCAwIDAgMCAyMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDU5LDEzMCwyNDYsMC4xKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-theme-surface rounded-lg transition-colors"
          >
            <X size={20} className="text-theme-secondary hover:text-theme-primary" />
          </button>

          {/* Icon central */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-orange-600/30 blur-3xl rounded-full animate-pulse" />
              <div className="relative bg-gradient-to-br from-orange-600 to-blue-600 p-4 rounded-2xl shadow-lg">
                <Zap size={32} className="text-theme-primary" />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          <h2 className="text-2xl font-bold text-theme-primary mb-3">
            Tokens insuffisants
          </h2>
          <p className="text-theme-secondary text-base mb-2">
            Cette action nécessite <span className="text-orange-600 font-semibold">{requiredTokens.toLocaleString('fr-FR')}</span> tokens.
          </p>
          <p className="text-theme-tertiary text-sm mb-6">
            Vous disposez actuellement de <span className="text-theme-primary font-semibold">{currentBalance.toLocaleString('fr-FR')}</span> tokens.
          </p>

          {/* Stats manquants */}
          <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20">
            <div className="flex items-center justify-center space-x-2 text-red-400">
              <Zap size={20} />
              <span className="font-semibold">
                Il vous manque {(requiredTokens - currentBalance).toLocaleString('fr-FR')} tokens
              </span>
            </div>
          </div>

          {/* Message selon le plan */}
          <div className="mb-6 p-6 rounded-xl bg-gradient-to-br from-orange-600/10 to-purple-500/10 border border-orange-600/20">
            <FileText className="w-12 h-12 text-orange-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-theme-primary mb-2">
              {subscriptionTier === 'free' 
                ? 'Passez à un plan payant pour débloquer toutes les fonctionnalités'
                : 'Rechargez vos tokens pour continuer'}
            </h3>
            <p className="text-sm text-theme-secondary mb-4">
              {subscriptionTier === 'free'
                ? 'Découvrez nos plans Pro, Business et Enterprise avec des milliers de tokens inclus chaque mois.'
                : 'Consultez nos plans pour augmenter votre allowance mensuelle ou rechargez des tokens supplémentaires.'}
            </p>
            <div className="flex items-center justify-center space-x-2 text-xs text-orange-600">
              <span>Plan Pro</span>
              <span className="text-neutral-600">•</span>
              <span>960K tokens/mois</span>
              <span className="text-neutral-600">•</span>
              <span>29€</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-theme-hover hover:bg-neutral-700 text-theme-primary font-semibold rounded-xl transition-all"
            >
              Annuler
            </button>
            <button
              onClick={handleNavigateToPricing}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-600 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-theme-primary font-bold rounded-xl transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center justify-center space-x-2"
            >
              <span>Voir les plans</span>
              <ArrowRight size={18} />
            </button>
          </div>

          <p className="text-xs text-theme-tertiary mt-4">
            Plans flexibles • Paiement sécurisé par Stripe
          </p>
        </div>
      </div>
    </div>
  );
}
